import { Play } from 'lucide-react';

import Icon from 'components/icon/Icon';
import { cn } from 'lib/utils';
import { VoiceReadingData } from 'types/voice';

interface ScanVitalDetailProps {
  category: string;
  selectedVital: VoiceReadingData | undefined;
  vital: VoiceReadingData;
  onSelect: (key: string) => void;
}

export default function ScanVitalDetail({
  category,
  vital,
  selectedVital,
  onSelect,
}: ScanVitalDetailProps) {
  const renderColorRange = (
    vital: VoiceReadingData | undefined,
    name?: string
  ) => {
    if (!vital) return null;

    return (
      <div
        className='relative grid items-end w-full'
        style={{
          gridTemplateColumns: `repeat(${vital?.colorRange?.length}, minmax(0, 1fr))`,
        }}
      >
        {vital?.colorRange?.map(({ color, category }, index: number) => (
          <div key={index} className='relative col-span-1'>
            <div
              style={{ backgroundColor: color }}
              className={cn('w-full h-2', category === vital.category && 'h-4')}
            />
            {category === vital.category && (
              <>
                <div
                  className={cn(
                    'absolute flex flex-col items-center -translate-x-1/2 left-1/2',
                    name ? '-top-7' : '-top-4'
                  )}
                >
                  {name ? (
                    <p className='-mb-1 text-sm leading-none'>{name}</p>
                  ) : null}
                  <Play
                    className='w-4 h-4 rotate-90'
                    style={{
                      color: vital.color,
                      fill: vital.color,
                    }}
                  />
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderIcon = (key: string, value: string | number, color: string) => (
    <Icon
      data-testid={`${key}-icon`}
      icon={key}
      size={28}
      color={value === 'Unknown' ? '#808080' : color}
    />
  );

  const renderCategory = (
    value: number | string,
    color: string,
    category: string
  ) => (
    <div
      className='col-span-2 text-sm font-semibold leading-tight text-center lg:col-span-1'
      style={{
        color: value === 'Unknown' ? '#808080' : color,
      }}
    >
      {category}
    </div>
  );

  return (
    <div className='transition-colors duration-300 cursor-pointer border-y border-y-primary'>
      <div
        className={cn(
          'grid items-center justify-between w-full grid-cols-8 gap-y-4 py-4',
          selectedVital?.key === vital.key && 'bg-primary/10'
        )}
        onClick={() => onSelect(vital.key)}
        data-testid={`${vital.key}-vital`}
      >
        <div className='flex items-center col-span-6 gap-2 lg:col-span-3'>
          {renderIcon(vital.key, vital.value, vital.color)}

          <div className='font-medium leading-none'>
            <h2>{vital?.display}</h2>

            <p className='font-semibold'>
              {vital.value}
              {category === 'Energy Levels' ? '%' : ''}
            </p>
          </div>
        </div>

        {renderCategory(vital.value, vital.color, vital.category)}

        <div className='flex justify-center mb-2 lg:mb-0 lg:justify-end col-span-full lg:col-span-4'>
          {renderColorRange(vital)}
        </div>
      </div>
    </div>
  );
}
